// src/components/admin/AdminOrderItem.js
import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue } from 'firebase/database';
import styles from './Admin.module.css';

const AdminOrderItem = ({ order, onUpdateStatus, onMarkAsPickedUp }) => {
  // ステータスの順序: Pending -> preparation -> Finish
  const [currentStage, setCurrentStage] = useState(() => {
    if (order.status === 'Finish') {
      return 'finish'; // 完了状態
    } else if (order.status === 'preparation') {
      return 'preparation'; // 製作中
    } else {
      return 'pending'; // 初期状態（未処理）
    }
  });

  // 商品の詳細情報（材料など）
  const [productDetails, setProductDetails] = useState(null);

  // 商品情報を取得
  useEffect(() => {
    if (order.productId) {
      const db = getDatabase();
      const productRef = ref(db, `products/${order.productId}`);

      onValue(productRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setProductDetails(data);
        }
      }, {
        onlyOnce: true
      });
    }
  }, [order.productId]);

  // 注文日時をフォーマット
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ja-JP', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // 注文からの経過時間を計算
  const getElapsedTime = (dateString) => {
    const orderDate = new Date(dateString);
    const now = new Date();
    const elapsed = now - orderDate;

    // ミリ秒を分に変換
    const minutes = Math.floor(elapsed / 60000);

    if (minutes < 1) {
      return "たった今";
    } else if (minutes < 60) {
      return `${minutes}分前`;
    } else {
      const hours = Math.floor(minutes / 60);
      return `${hours}時間${minutes % 60}分前`;
    }
  };

  // 材料情報を表示
  const renderIngredients = () => {
    if (!productDetails || !productDetails.ingredients) {
      return <p className={styles.recipeText}>材料情報がありません</p>;
    }

    const { main, secondary } = productDetails.ingredients;

    return (
      <div className={styles.recipeInfo}>
        <p className={styles.recipeText}>
          <span className={styles.recipeTitle}>作り方:</span>
          {main && `${main.name} ${main.amount} `}
          {secondary && `+ ${secondary.name} ${secondary.amount}`}
          を混ぜてください
        </p>
      </div>
    );
  };

  // ボタンをクリックしたときの処理
  const handleActionButton = () => {
    if (currentStage === 'pending') {
      // 製作開始
      onUpdateStatus(order.id, 'preparation');
      setCurrentStage('preparation');
    } else if (currentStage === 'preparation') {
      // 準備完了 - isReadyもtrueに設定
      onUpdateStatus(order.id, 'Finish', true); // isReadyをtrueに設定するフラグを追加
      setCurrentStage('finish');
    }
  };

  // ボタンの表示とスタイルを管理
  const buttonConfig = {
    pending: {
      text: '製作開始',
      style: styles.actionButtonInitial,
      icon: '🔄' // オプションでアイコンを追加
    },
    preparation: {
      text: '準備完了',
      style: styles.actionButtonPreparing,
      icon: '✓' // オプションでアイコンを追加
    },
    finish: {
      text: '完了済み',
      style: styles.actionButtonReady,
      icon: '✓' // オプションでアイコンを追加
    }
  };

  // 現在のステージに応じたボタン設定を取得
  const getCurrentButtonConfig = () => {
    return buttonConfig[currentStage] || buttonConfig.initial;
  };

  // 準備完了の場合はコンパクト表示（処理中タブでは「受け取り完了にする」ボタンを表示）
  if (currentStage === 'finish') {
    const canMarkPickedUp = !order.isPickedUp && typeof onMarkAsPickedUp === 'function';
    return (
      <div className={styles.compactOrderItem}>
        <div className={styles.compactOrderContent}>
          <div className={styles.compactOrderInfo}>
            <div className={styles.compactProductInfo}>
              <span className={styles.compactProductName}>{order.productName}</span>
              <span className={styles.compactCustomerName}>({order.userName})</span>
            </div>
            <div className={styles.compactOrderMeta}>
              {formatDate(order.orderedAt)}
              <span className={styles.compactStatusText}>準備完了</span>
            </div>
          </div>

          <div className={styles.compactOrderActions}>
            {canMarkPickedUp && (
              <button
                type="button"
                onClick={() => onMarkAsPickedUp(order)}
                className={styles.pickupCompleteButton}
                title="ユーザーが受け取りを忘れた場合に強制で完了にします"
              >
                受け取り完了にする
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 通常表示（未処理・製作中の注文用）
  return (
    <div className={styles.orderItem}>
      <div className={styles.orderContent}>
        {/* 商品画像（準備完了前のみ表示） - 1:1比率で左側に配置 */}
        <div className={styles.productImage}>
          <img
            src={order.productImage || "/api/placeholder/100/100"}
            alt={order.productName}
            className={styles.productImageInner}
          />
        </div>

        <div className={styles.orderInfo}>
          <div className={styles.orderHeader}>
            <h3 className={styles.productName}>{order.productName}</h3>
            <div className={styles.orderTimeHeader}>
              <span>{formatDate(order.orderedAt)}</span>
              <span className={styles.orderElapsedHeader}>{getElapsedTime(order.orderedAt)}</span>
            </div>
          </div>

          <div className={styles.orderCustomer}>
            <span className={styles.customerLabel}>注文者: </span>
            {order.userName || order.userEmail}
          </div>

          {/* 材料情報とボタンを横並びに配置 */}
          <div className={styles.actionRow}>
            {/* 材料情報を表示 */}
            <div className={styles.recipeContainer}>
              {renderIngredients()}
            </div>

            <div className={styles.buttonWrapper}>
              <button
                onClick={handleActionButton}
                disabled={currentStage === 'finish'}
                className={`${styles.actionButton} ${getCurrentButtonConfig().style}`}
              >
                <span className={styles.buttonText}>
                  {getCurrentButtonConfig().text}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrderItem;